// biome-ignore-all lint/style/useConsistentArrowReturn: block-bodied mapping helpers kept as written
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import React from "react";

import { PostedState } from "../../posting";
import RequeueHint from "../../RequeueHint";

const POSTING_STATES: Set<string> = new Set([
  PostedState[PostedState.PENDING],
  PostedState[PostedState.POSTING],
  PostedState[PostedState.REQUEUED],
]);

const UNPOSTED_STATES: Set<string> = new Set([
  PostedState[PostedState.UNPOSTED],
  PostedState[PostedState.RETRY],
]);

const postedStateToText = (postedState: string) => {
  switch (postedState) {
    case PostedState[PostedState.AUTO_POSTED]:
      return "Auto Posted";
    case PostedState[PostedState.MANUALLY_POSTED]:
      return "Manually Posted";
    case PostedState[PostedState.FAILURE]:
      return "Failure";
    case PostedState[PostedState.PENDING]:
      return "Pending";
    case PostedState[PostedState.POSTING]:
      return "Posting";
    case PostedState[PostedState.RETRY]:
      return "Retry";
    case PostedState[PostedState.REQUEUED]:
      return "Pending";
    default:
      return "Post";
  }
};

const postedStateToColor = (postedState: string) => {
  switch (postedState) {
    case PostedState[PostedState.AUTO_POSTED]:
      return "success";
    case PostedState[PostedState.MANUALLY_POSTED]:
      return "success";
    case PostedState[PostedState.FAILURE]:
      return "error";
    case PostedState[PostedState.PENDING]:
      return "warning";
    case PostedState[PostedState.POSTING]:
      return "primary";
    case PostedState[PostedState.RETRY]:
      return "warning";
    case PostedState[PostedState.REQUEUED]:
      return "warning";
    default:
      return "primary";
  }
};

const postedStateToVariant = (postedState: string) => {
  switch (postedState) {
    case PostedState[PostedState.AUTO_POSTED]:
      return "text";
    case PostedState[PostedState.MANUALLY_POSTED]:
      return "text";
    default:
      return "contained";
  }
};

const getToolTipText = (postedLatestErrorMessage: string | null) => {
  if (postedLatestErrorMessage) {
    return postedLatestErrorMessage;
  }
  return "Post to PMS";
};

const renderMenuItems = (
  postedState: string,
  onPost: () => void,
  onManualPost: () => void,
) => {
  return (
    <>
      {UNPOSTED_STATES.has(postedState) && (
        <MenuItem data-testid="autoPost" onClick={onPost}>
          Auto Post
        </MenuItem>
      )}
      {(UNPOSTED_STATES.has(postedState) ||
        postedState === PostedState[PostedState.FAILURE]) && (
        <MenuItem data-testid="manualPost" onClick={onManualPost}>
          Manually Post
        </MenuItem>
      )}
    </>
  );
};

export default function PostButton({
  isPosting,
  onPost,
  onManualPost,
  postedState,
  verificationStatus,
  postedLatestErrorMessage,
  openUpward = false,
}: {
  isPosting: boolean;
  onPost: () => void;
  onManualPost: () => void;
  postedState: string;
  verificationStatus: string | null;
  postedLatestErrorMessage: string | null;
  openUpward?: boolean;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (POSTING_STATES.has(postedState) || isPosting) {
      return;
    }
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleAutoPost = () => {
    onPost();
    handleClose();
  };

  const handleManualPost = () => {
    onManualPost();
    handleClose();
  };

  const isWaiting =
    postedState === PostedState[PostedState.PENDING] ||
    postedState === PostedState[PostedState.REQUEUED];

  return (
    <>
      <Tooltip
        title={getToolTipText(postedLatestErrorMessage)}
        TransitionComponent={Zoom}
        placement="top"
        arrow
      >
        <Button
          data-testid="postButton"
          color={postedStateToColor(postedState)}
          variant={postedStateToVariant(postedState)}
          endIcon={<ArrowDropDownIcon />}
          onClick={handleClick}
          disabled={
            POSTING_STATES.has(postedState) ||
            isPosting ||
            verificationStatus !== "COMPLETED"
          }
        >
          {postedStateToText(postedState)}
        </Button>
      </Tooltip>
      {isWaiting && (
        <RequeueHint
          requeue={
            postedState === PostedState[PostedState.REQUEUED]
              ? { reason: postedLatestErrorMessage, at: null }
              : undefined
          }
        />
      )}
      <Menu
        id="pms-menu"
        aria-labelledby="pms-menu"
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: openUpward ? "top" : "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: openUpward ? "bottom" : "top",
          horizontal: "left",
        }}
        sx={{
          marginTop: openUpward ? "-0.5rem" : "1rem",
        }}
      >
        {renderMenuItems(postedState, handleAutoPost, handleManualPost)}
      </Menu>
    </>
  );
}
